/* eslint-disable react/require-default-props */
// import dropboxLogo from '@src/assets/platform-logos/dropbox-logo.png';
// import googleDriveLogo from '@src/assets/platform-logos/google-drive-logo.png';
import zoomLogo from '@src/assets/platform-logos/zoom-logo.png';
import ItemDropdownSelect from '@src/components/common/ItemDropdownSelect';
import { ORIGINAL_VIDEO_LANGUAGE_OPTIONS } from '@src/config/languages';
import useVideoCreatorDataInitialization from '@src/hooks/useVideoCreatorDataInitialization';
import { useDatoPerson, useStore } from '@src/stores-v2/StoreContext';
import CircleArrowRightIcon from '@src/svgs/CircleArrowRightIcon';
import LongArrowUpIcon from '@src/svgs/LongArrowUpIcon';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import DragAndDrop from '../../common/DragAndDrop';
import MultiFileUploadConcat from '../../common/MultiFileUploadConcat';
import { OptionItem, OptionList } from '../../common/OptionList';
import PrimaryActionButton from '../../sidepanel/PrimaryActionButton';
import BRollUploading from './BRollUploading';
import ZoomAuthButton from './ZoomAuthButton';
import ZoomRecordingsEnhanced from './ZoomRecordingsEnhanced';

import type {
  ArtifactFile,
  ArtifactUpload,
  StoryVideoMusicStrategy,
} from './useStoryCreator';

type StoryTypeOption = {
  value: 'produced' | 'raw';
  text: string;
};

const STORY_TYPE_OPTIONS: StoryTypeOption[] = [
  {
    value: 'produced',
    text: 'I have produced content',
  },
  {
    value: 'raw',
    text: 'I have raw content',
  },
];

type StoryVideoMusicStrategyOption = {
  value: StoryVideoMusicStrategy;
  text: string;
};

const STORY_VIDEO_MUSIC_STRATEGY_OPTIONS_PRODUCED: StoryVideoMusicStrategyOption[] =
  [
    {
      value: 'process',
      text: 'Remove music',
    },
    {
      value: 'keep',
      text: 'Keep my music',
    },
  ];

const STORY_VIDEO_MUSIC_STRATEGY_OPTIONS_RAW: StoryVideoMusicStrategyOption[] =
  [
    {
      value: 'process',
      text: 'Correct audio',
    },
    {
      value: 'keep',
      text: 'Leave audio',
    },
  ];

const MUSIC_OPTIONS_TITLE: Record<'raw' | 'produced', string> = {
  produced: 'Music',
  raw: 'Audio',
};

const MUSIC_OPTIONS_LIST: Record<
  'raw' | 'produced',
  StoryVideoMusicStrategyOption[]
> = {
  produced: STORY_VIDEO_MUSIC_STRATEGY_OPTIONS_PRODUCED,
  raw: STORY_VIDEO_MUSIC_STRATEGY_OPTIONS_RAW,
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 8px;
  gap: 8px;
`;

const Title = styled.h2`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 700;
  font-size: 32px;
  line-height: 38.73px;
  text-align: center;
  color: #f3e9d7;
  margin: 0;
`;

const Subheading = styled.h3`
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  text-align: center;
  color: #f3e9d7;
  margin: 0;
`;

const ProceedButton = styled(PrimaryActionButton)`
  width: 100%;
  font-weight: 700;
  font-size: 14px;
  line-height: 17px;
`;

const UploadArea = styled.div<{ padded?: boolean }>`
  padding: ${props => (props.padded ? '8px 0' : '0')};
`;

const Hint = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
  > .upload-msg {
    color: #f3e9d7;
    font-size: 14px;
    font-weight: 700;
    line-height: 140%;
  }
  > .upload-limits {
    color: #ef5d6f;
    line-height: 140%;
  }
`;

const FieldSet = styled.div`
  margin: 0 0 16px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
`;

const FieldName = styled.div`
  color: #f3e9d7;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const Input = styled.input`
  border-radius: 8px;
  border: 1px solid #484848;
  padding: 16px;
  color: #f3e9d7;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  background: #03041a;
`;

const DropdownWrapper = styled.div`
  color: #f3e9d7;
  .Selected {
    padding: 16px;
  }
`;

const CharacterCount = styled.div<{ isError: boolean }>`
  color: ${props => (props.isError ? '#ef5d6f' : '#808080')};
  font-size: 12px;
  font-weight: 400;
  margin-top: 4px;
  text-align: right;
`;

const UploadModeSelector = styled.div``;

const UrlInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid #484848;
  padding: 12px 16px;
  color: #f3e9d7;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  background: #03041a;

  &::placeholder {
    color: #808080;
  }

  &:focus {
    outline: none;
    border-color: #45d483;
  }
`;

const UrlInputField = styled.input`
  flex: 1;
  padding: 16px 0;
  border: none;
  outline: none;
  color: #f3e9d7;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  background: #03041a;

  &::placeholder {
    color: #848484;
  }
`;

const ProceedIconButton = styled.button`
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;

  img {
    height: 20px;
    width: auto;
    object-fit: contain;
    opacity: 0.6;
  }
`;

const Divider = styled.div`
  position: relative;
  text-align: center;
  margin: 24px 0;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: #484848;
  }

  span {
    position: relative;
    background: #03041a;
    padding: 0 16px;
    color: #808080;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;

const ZoomOptionTitle = styled.div`
  color: #f3e9d7;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;

  img {
    height: 16px;
    width: auto;
  }
`;

const BackButton = styled.button`
  background: none;
  padding: 12px;
  border: none;
  cursor: pointer;
  position: absolute;
  top: 10px;
  left: 12px;
  transform: rotate(-90deg);
`;

const ConnectButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 16px;
  background: transparent;
  color: #848484;
  border: 1px solid #848484;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 400;
  line-height: 140%;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: rgba(69, 212, 131, 0.1);
    border-color: #45d483;
    color: #45d483;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1a1a2e;
  border-radius: 12px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  border: 1px solid #484848;
`;

const ModalTitle = styled.h3`
  color: #f3e9d7;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 12px 0;
`;

const ModalText = styled.p`
  color: #a0a0a0;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 24px 0;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ModalButton = styled.button<{ secondary?: boolean; danger?: boolean }>`
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;

  ${props =>
    props.secondary &&
    `
    background: transparent;
    color: #a0a0a0;
    border-color: #484848;

    &:hover {
      background: #2a2a2a;
      border-color: #606060;
    }
  `}

  ${props =>
    props.danger &&
    `
    background: transparent;
    color: #ef5d6f;
    border-color: #ef5d6f;

    &:hover {
      background: rgba(239, 93, 111, 0.1);
      border-color: #ff6b7d;
    }
  `}
`;

export function StepOne({
  goToNextStep,
  canGoToStep,
  handleStoryVideoUpload,
  handleStoryUrlUpload,
  maxUploadSizeGB,
}: {
  goToNextStep: () => void;
  canGoToStep: boolean;
  handleStoryVideoUpload: (f: File[]) => void;
  handleStoryUrlUpload?: (url: string) => void;
  maxUploadSizeGB: number;
}) {
  useVideoCreatorDataInitialization();

  const { userIdentityStore } = useStore();
  const datoPerson = useDatoPerson();
  const { enableMultiFileConcat, enableUrlUpload, enableZoomConnection } =
    useFlagsCombination(userIdentityStore.currentIdentity?.role as any);

  const [uploadMode, setUploadMode] = useState<'single' | 'multi'>('single');
  const [urlInput, setUrlInput] = useState<string>('');
  const [urlError, setUrlError] = useState<string>('');
  const [showZoomRecordings, setShowZoomRecordings] = useState<boolean>(false);
  const [isZoomAuthenticated, setIsZoomAuthenticated] =
    useState<boolean>(false);
  const [selectedZoomRecording, setSelectedZoomRecording] = useState<any>(null);
  const [showDisconnectModal, setShowDisconnectModal] =
    useState<boolean>(false);

  // Check if Zoom is already authenticated
  useEffect(() => {
    // Check Zoom authentication status
    const checkZoomAuth = async () => {
      // First check datoPerson for quick local check
      if (datoPerson?.hasZoomInfo() && !datoPerson?.isZoomTokenExpired()) {
        setIsZoomAuthenticated(true);
        return;
      }

      // If datoPerson shows expired or no info, check with API which will refresh if needed
      const personId = datoPerson?.person?.id;
      const email =
        userIdentityStore.currentIdentity?.email || datoPerson?.person?.email;

      if (personId || email) {
        try {
          const params = new URLSearchParams();
          if (personId) params.append('personId', personId);
          if (email) params.append('email', email);

          const apiUrl = process.env.REACT_APP_API_URL || '';
          const response = await fetch(
            `${apiUrl}/api/zoom/get-zoom-info?${params.toString()}`,
          );

          if (response.ok) {
            const data = await response.json();
            setIsZoomAuthenticated(data.authenticated);

            // If token was refreshed, log it
            if (data.authenticated && data.refreshed) {
              console.log('Zoom token was automatically refreshed');
            }
          } else {
            setIsZoomAuthenticated(false);
          }
        } catch (error) {
          console.error('Failed to check Zoom auth:', error);
          // Fall back to datoPerson check
          if (datoPerson?.hasZoomInfo() && !datoPerson?.isZoomTokenExpired()) {
            setIsZoomAuthenticated(true);
          } else if (!datoPerson?.hasZoomInfo() && isZoomAuthenticated) {
            // Keep authenticated state temporarily
            console.log(
              'Auth state mismatch - keeping authenticated state temporarily',
            );
          }
        }
      } else if (!datoPerson?.hasZoomInfo() && isZoomAuthenticated) {
        // Keep authenticated state if we just authenticated
        console.log(
          'Auth state mismatch - keeping authenticated state temporarily',
        );
      }
    };

    checkZoomAuth();
  }, [datoPerson, userIdentityStore.currentIdentity?.email]);

  const validateUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleProceed = async () => {
    // If a Zoom recording is selected, process it
    if (selectedZoomRecording) {
      await processZoomRecording();
    }
    // If URL upload is enabled and URL is provided, use URL upload
    else if (enableUrlUpload && urlInput.trim()) {
      setUrlError('');
      if (!validateUrl(urlInput)) {
        setUrlError('Please enter a valid URL');
        return;
      }
      if (handleStoryUrlUpload) {
        handleStoryUrlUpload(urlInput);
      }
    } else {
      // Otherwise proceed with file upload
      goToNextStep();
    }
  };

  const handleZoomRecordingSelect = async (recording: any) => {
    // Store the selected recording
    setSelectedZoomRecording(recording);
    // This makes the Next button enabled
  };

  const processZoomRecording = async () => {
    if (!selectedZoomRecording) return;

    // Get user identity info for authentication
    const personId =
      datoPerson?.id || userIdentityStore.currentIdentity?.personId;
    const email = userIdentityStore.currentIdentity?.email;

    // Convert Zoom recording to URL for processing
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const response = await fetch(
      `${apiUrl}/api/zoom/create-story-from-recording`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userIdentityStore.currentIdentity?.token}`,
        },
        body: JSON.stringify({
          recordingId: selectedZoomRecording.id,
          recordingFiles: selectedZoomRecording.recording_files,
          personId: personId,
          email: email,
          password: selectedZoomRecording.password, // Include password for protected recordings
        }),
      },
    );

    if (response.ok) {
      const data = await response.json();
      // Use the video URL to create the story (Lambda will handle download)
      if (handleStoryUrlUpload && data.data.videoUrl) {
        // Reset Zoom recording state before moving to next step
        setShowZoomRecordings(false);
        setSelectedZoomRecording(null);

        handleStoryUrlUpload(data.data.videoUrl);
        // Pass the password along if present
        if (data.data.platformPassword) {
          // Store password to be used in the upload-task call
          sessionStorage.setItem(
            'zoom_recording_password',
            data.data.platformPassword,
          );
        }
      }
    } else {
      console.error('Failed to prepare Zoom recording');
      const errorData = await response.json();
      if (errorData.code === 'ZOOM_AUTH_REQUIRED') {
        // Token expired or missing, need to re-authenticate
        setIsZoomAuthenticated(false);
        setShowZoomRecordings(false);
      }
    }
  };

  const handleZoomAuthSuccess = () => {
    setIsZoomAuthenticated(true);
    // Force a re-check of the person data after a short delay
    setTimeout(() => {
      if (datoPerson?.hasZoomInfo() && !datoPerson?.isZoomTokenExpired()) {
        setIsZoomAuthenticated(true);
      }
    }, 1000);
    // Don't automatically show recordings, let user click the button
  };

  return (
    <Wrapper>
      <TitleWrapper>
        <Title>
          {showZoomRecordings ? (
            <>
              Import from <img src={zoomLogo} alt="Zoom" width={120} />
            </>
          ) : (
            'Upload your video'
          )}
        </Title>
        {uploadMode === 'multi' && (
          <Subheading>Combine multiple videos into a single video</Subheading>
        )}
      </TitleWrapper>
      {!showZoomRecordings && enableMultiFileConcat && (
        <UploadModeSelector>
          <OptionList>
            <OptionItem
              fontSize="14px"
              isCurrent={uploadMode === 'single'}
              onClick={() => setUploadMode('single')}
            >
              Single Video
            </OptionItem>
            <OptionItem
              fontSize="14px"
              isCurrent={uploadMode === 'multi'}
              onClick={() => setUploadMode('multi')}
            >
              Multiple Videos
            </OptionItem>
          </OptionList>
        </UploadModeSelector>
      )}
      {!showZoomRecordings && (
        <UploadArea padded>
          {uploadMode === 'single' ? (
            <DragAndDrop
              width={440}
              height={120}
              callback={handleStoryVideoUpload}
              acceptType="video"
              multiple={false}
              videoFormats={['video/mp4', 'video/mov']}
              description={
                <Hint>
                  <span className="upload-msg">Drop your video file here</span>
                  <span
                    className="upload-limits"
                    style={{ fontSize: '12px', color: '#808080' }}
                  >
                    {`Max file size is ${maxUploadSizeGB} GB`}
                  </span>
                </Hint>
              }
            />
          ) : (
            <MultiFileUploadConcat
              onFilesChange={handleStoryVideoUpload}
              maxUploadSizeGB={maxUploadSizeGB}
            />
          )}
        </UploadArea>
      )}

      {enableUrlUpload && (
        <>
          {!showZoomRecordings || !enableZoomConnection ? (
            <div>
              <div style={{ marginBottom: '20px' }}>
                <UrlInputWrapper>
                  <svg
                    style={{ width: '20px', height: '20px', fill: '#808080' }}
                    viewBox="0 0 24 24"
                  >
                    <path d="M10.59 13.41c.41.39.41 1.03 0 1.42-.39.39-1.03.39-1.42 0a5.003 5.003 0 0 1 0-7.07l3.54-3.54a5.003 5.003 0 0 1 7.07 0 5.003 5.003 0 0 1 0 7.07l-1.49 1.49c.01-.82-.12-1.64-.4-2.42l.47-.48a2.982 2.982 0 0 0 0-4.24 2.982 2.982 0 0 0-4.24 0l-3.53 3.53a2.982 2.982 0 0 0 0 4.24m2.82-4.24c.39-.39 1.03-.39 1.42 0a5.003 5.003 0 0 1 0 7.07l-3.54 3.54a5.003 5.003 0 0 1-7.07 0 5.003 5.003 0 0 1 0-7.07l1.49-1.49c-.01.82.12 1.64.4 2.42l-.47.48a2.982 2.982 0 0 0 0 4.24 2.982 2.982 0 0 0 4.24 0l3.53-3.53a2.982 2.982 0 0 0 0-4.24 2.982 2.982 0 0 0-1.42 0z" />
                  </svg>
                  <UrlInputField
                    type="url"
                    placeholder="Or drop a link from Zoom, Google Drive, or Dropbox"
                    value={urlInput}
                    onChange={e => {
                      setUrlInput(e.target.value);
                      setUrlError('');
                    }}
                    onKeyPress={e => {
                      if (e.key === 'Enter') {
                        handleProceed();
                      }
                    }}
                  />
                  <ProceedIconButton
                    disabled={!urlInput.trim()}
                    onClick={handleProceed}
                  >
                    <CircleArrowRightIcon
                      fillColor={!urlInput.trim() ? '#848484' : '#17C964'}
                    />
                  </ProceedIconButton>
                </UrlInputWrapper>
                {urlError && (
                  <div
                    style={{
                      color: '#ef5d6f',
                      fontSize: '12px',
                      marginTop: '8px',
                    }}
                  >
                    {urlError}
                  </div>
                )}
              </div>

              {enableZoomConnection && (
                <>
                  <Divider>
                    <span>or</span>
                  </Divider>

                  <div style={{ marginTop: '20px' }}>
                    {!isZoomAuthenticated ? (
                      <ZoomAuthButton onAuthSuccess={handleZoomAuthSuccess} />
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          width: '100%',
                        }}
                      >
                        <ConnectButton
                          onClick={() => setShowZoomRecordings(true)}
                          style={{ flex: 1 }}
                        >
                          View Recordings from
                          <img src={zoomLogo} alt="Zoom" />
                        </ConnectButton>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div>
              <div
                style={{
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <ZoomOptionTitle>Select a recording</ZoomOptionTitle>
              </div>
              <ZoomRecordingsEnhanced
                onSelectRecording={handleZoomRecordingSelect}
              />
              <BackButton
                onClick={() => {
                  setShowZoomRecordings(false);
                  setSelectedZoomRecording(null);
                }}
              >
                <LongArrowUpIcon width="16" height="16" />
              </BackButton>
            </div>
          )}
        </>
      )}
      <ProceedButton
        onClick={handleProceed}
        disabled={
          !canGoToStep &&
          !(enableUrlUpload && urlInput.trim()) &&
          !selectedZoomRecording
        }
        isActivated
        style={{ marginTop: '8px' }}
      >
        {selectedZoomRecording
          ? 'Next'
          : enableUrlUpload && urlInput.trim()
            ? 'Import'
            : 'Next'}
      </ProceedButton>

      {showDisconnectModal && (
        <ModalOverlay onClick={() => setShowDisconnectModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalTitle>Disconnect Zoom Account?</ModalTitle>
            <ModalText>
              Are you sure you want to disconnect your Zoom account? You'll need
              to reconnect to import recordings again.
            </ModalText>
            <ModalButtons>
              <ModalButton
                secondary
                onClick={() => setShowDisconnectModal(false)}
              >
                Cancel
              </ModalButton>
              <ModalButton
                danger
                onClick={async () => {
                  try {
                    const apiUrl = process.env.REACT_APP_API_URL || '';
                    const response = await fetch(
                      `${apiUrl}/api/zoom/disconnect`,
                      {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          Authorization: `Bearer ${userIdentityStore.currentIdentity?.token}`,
                        },
                        body: JSON.stringify({
                          personId: datoPerson?.person?.id,
                          email: userIdentityStore.currentIdentity?.email,
                        }),
                      },
                    );

                    if (response.ok) {
                      // Clear local state
                      setIsZoomAuthenticated(false);
                      setShowZoomRecordings(false);
                      setSelectedZoomRecording(null);

                      // Clear zoom info from datoPerson if available
                      if (datoPerson?.updateZoomInfo) {
                        await datoPerson.updateZoomInfo(null);
                      }
                    } else {
                      console.error(
                        'Failed to disconnect Zoom:',
                        await response.text(),
                      );
                    }
                  } catch (error) {
                    console.error('Failed to disconnect Zoom:', error);
                  }

                  // Always close modal
                  setShowDisconnectModal(false);
                }}
              >
                Disconnect
              </ModalButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </Wrapper>
  );
}

export function StepTwo({
  goToNextStep,
  canGoToStep,
  storyType,
  setStoryType,
  videoMusicStrategy,
  setVideoMusicStrategy,
}: {
  goToNextStep: () => void;
  canGoToStep: boolean;
  storyType: 'raw' | 'produced' | null;
  setStoryType: (t: 'raw' | 'produced') => void;
  videoMusicStrategy: StoryVideoMusicStrategy;
  setVideoMusicStrategy: (s: StoryVideoMusicStrategy) => void;
}) {
  return (
    <>
      <Title>Story Details</Title>
      <FieldSet>
        <FieldName>Story Type</FieldName>
        <OptionList>
          {STORY_TYPE_OPTIONS.map(o => (
            <OptionItem
              isCurrent={o.value === storyType}
              onClick={() => setStoryType(o.value)}
              key={o.value}
            >
              {o.text}
            </OptionItem>
          ))}
        </OptionList>
      </FieldSet>
      {storyType && (
        <FieldSet>
          <FieldName>{MUSIC_OPTIONS_TITLE[storyType]}</FieldName>
          <OptionList>
            {MUSIC_OPTIONS_LIST[storyType].map(o => (
              <OptionItem
                isCurrent={o.value === videoMusicStrategy}
                onClick={() => setVideoMusicStrategy(o.value)}
                key={o.value}
              >
                {o.text}
              </OptionItem>
            ))}
          </OptionList>
        </FieldSet>
      )}
      <ProceedButton onClick={goToNextStep} disabled={!canGoToStep} isActivated>
        Next
      </ProceedButton>
    </>
  );
}

export function StepThree({
  goToNextStep,
  canGoToStep,
  storyName,
  setStoryName,
  storyLanguage,
  setStoryLanguage,
  allowLangSelect = false,
  maxStoryTitleLength,
}: {
  goToNextStep: () => void;
  canGoToStep: boolean;
  storyName: string;
  setStoryName: (s: string) => void;
  storyLanguage: string | undefined;
  setStoryLanguage: (s: string) => void;
  allowLangSelect?: boolean;
  maxStoryTitleLength: number;
}) {
  useEffect(() => {
    // so that page scrolling doesn't interfere with dropdown scrolling
    const overlow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overlow;
    };
  }, []);

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        if (canGoToStep) {
          goToNextStep();
        }
      }}
    >
      <Title>Story Details</Title>
      <FieldSet>
        <FieldName>Story Name</FieldName>
        <Input
          required
          placeholder="Name your story"
          value={storyName}
          onKeyDown={e => e.stopPropagation()}
          onChange={e => setStoryName(e.target.value)}
          maxLength={maxStoryTitleLength}
        />
        <CharacterCount isError={storyName.length > maxStoryTitleLength}>
          {storyName.length}/{maxStoryTitleLength} characters
        </CharacterCount>
      </FieldSet>
      {allowLangSelect && (
        <FieldSet
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <FieldName>Transcript Language</FieldName>
          <DropdownWrapper>
            <ItemDropdownSelect
              action={(_, value) => value && setStoryLanguage(value)}
              propertyName="storyLanguage"
              defaultValue={storyLanguage}
              values={ORIGINAL_VIDEO_LANGUAGE_OPTIONS}
            />
          </DropdownWrapper>
        </FieldSet>
      )}
      <ProceedButton isActivated disabled={!canGoToStep} type="submit">
        Next
      </ProceedButton>
    </form>
  );
}

export function StepFour({
  goToNextStep,
  handleStoryBRollUpload,
  storyArtifactsFiles,
  storyArtifacts,
  storyArtifactsVideoFiles,
  storyArtifactsVideo,
  saveArtifactNotes,
  setArtifactNotes,
  deleteArtifact,
  saveArtifactVideoNotes,
  setArtifactVideoNotes,
  deleteArtifactVideo,
}: {
  goToNextStep: () => void;
  handleStoryBRollUpload: (f: File[]) => void;
  storyArtifactsFiles: ArtifactFile[];
  storyArtifactsVideoFiles: ArtifactFile[];
  storyArtifacts: ArtifactUpload[];
  storyArtifactsVideo: ArtifactUpload[];
  saveArtifactNotes: (internalId: string) => void;
  setArtifactNotes: (internalId: string, notes: string) => void;
  deleteArtifact: (internalId: string) => void;
  saveArtifactVideoNotes: (internalId: string) => void;
  setArtifactVideoNotes: (internalId: string, notes: string) => void;
  deleteArtifactVideo: (internalId: string) => void;
}) {
  const isNextButtonActive =
    storyArtifactsFiles.length === storyArtifacts.length &&
    storyArtifactsVideoFiles.length === storyArtifactsVideo.length;
  return (
    <>
      <Title>Let&apos;s collect BRoll</Title>
      {storyArtifactsFiles.length === 0 &&
      storyArtifactsVideoFiles.length === 0 ? (
        <UploadArea padded>
          <DragAndDrop
            width={440}
            callback={handleStoryBRollUpload}
            acceptType="all"
            multiple
            description={
              <Hint>
                <span className="upload-msg">
                  Or upload your videos and photos here.
                </span>
                <span className="upload-limits">
                  (Max file size is 100 MB per file)
                </span>
              </Hint>
            }
          />
        </UploadArea>
      ) : (
        <BRollUploading
          handleStoryBRollUpload={handleStoryBRollUpload}
          storyArtifactsFiles={storyArtifactsFiles}
          storyArtifactsVideoFiles={storyArtifactsVideoFiles}
          storyArtifacts={storyArtifacts}
          storyArtifactsVideo={storyArtifactsVideo}
          saveArtifactNotes={saveArtifactNotes}
          setArtifactNotes={setArtifactNotes}
          deleteArtifact={deleteArtifact}
          saveArtifactVideoNotes={saveArtifactVideoNotes}
          setArtifactVideoNotes={setArtifactVideoNotes}
          deleteArtifactVideo={deleteArtifactVideo}
        />
      )}
      <ProceedButton
        onClick={isNextButtonActive ? goToNextStep : () => {}}
        disabled={!isNextButtonActive}
      >
        Next
      </ProceedButton>
    </>
  );
}
